import React, { useEffect, useRef, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import UpdateCertifications from '@/pages/shell/workerManagement/cv-manager/index';
import Input from '@/shared/inputfield';
import ContentContainer from '@/components/containers/contentContainer';
import { SettingsBlackIcon, SettingsWhiteIcon } from '@/components/assets/icons/svgs';
import CustomButton from '@/shared/button/customButton';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '@/utils/api';
import { toast } from "@/utils/appToast";
import Divider from '@mui/material/Divider';
import PhoneInput from 'react-phone-input-2';
import Select from '@/shared/select/select';
import ReactSelect from 'react-select';
import { components } from 'react-select';
import TextArea from '@/shared/textArea/textArea';
import { updateUser } from '@/features/user/userSlice';
import { WorkerProfile } from '@/types/userTypes';
import { User } from '@/types/userTypes';
import { SVGUploadLogoImage } from '@/components/assets/register/svgs';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import type { GoogleLocation } from '@/types/googleLocationTypes';
import Resizer from "react-image-file-resizer";
import { useTranslation } from 'react-i18next';
import { isWorkerProfileComplete, getMissingFields } from '@/utils/workerProfileUtils';
import { getErrorMessage } from '@/utils/backendErrorMessage';
import Popup from '@/shared/popups/popupDetails';

const SettingsPage = () => {
    const { t, i18n } = useTranslation();
    const [tab, setTab] = useState(0);
    const [isSmallDevice, setIsSmallDevice] = useState(false);
    const [form, setForm] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', avatar: '' });
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const { user } = useSelector((state: any) => state.user || {});
    const [isProfileSubmitting, setIsProfileSubmitting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSkillsSubmitting, setIsSkillsSubmitting] = useState(false);
    const [selectedServices, setSelectedServices] = useState<any[]>([]);
    const [regions, setRegions] = useState([]);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [selectedService, setSelectedService] = useState<string>("");
    const [groupedRegionOptions, setGroupedRegionOptions] = useState<any[]>([]);
    const [selectedMunicipalities, setSelectedMunicipalities] = useState<any[]>([]);
    const [availability, setAvailability] = useState<string>("");
    const [messageText, setMessageText] = useState('');
    const dispatch = useDispatch();
    const [workerProfiles, setWorkerProfile] = useState<WorkerProfile>();
    const [services, setServices] = useState<any[]>([]);
    // --- Skills & Services State ---
    const [categories, setCategories] = useState<any[]>([]); // from getServiceCategories
    const [allServices, setAllServices] = useState<any[]>([]); // from getServices
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const [description, setDescription] = useState('');
    const [isProfileComplete, setIsProfileComplete] = useState(true);
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [ageConfirmed, setAgeConfirmed] = useState(false);
    // SMS notifications preference (both client and worker)
    const [smsNotificationsOptIn, setSmsNotificationsOptIn] = useState(false);
    // Profile Picture State
    const [imgPreview, setImgPreview] = useState<string>("");
    // Additional Profile Fields State
    const [hourlyPay, setHourlyPay] = useState<string>(null);
    const [physicalAddress, setPhysicalAddress] = useState({ line1: '', line2: '', city: '', zipCode: '' });
    const [postalAddress, setPostalAddress] = useState('');
    const [googleLocation, setGoogleLocation] = useState<GoogleLocation>();
    const [paymentType, setPaymentType] = useState<string>('');
    const [savedPaymentType, setSavedPaymentType] = useState<string>('');
    const [athPhoneNumber, setAthPhoneNumber] = useState<string>('');
    const [isPaymentTabSubmitting, setIsPaymentTabSubmitting] = useState(false);
    const [showStripeSwitchWarning, setShowStripeSwitchWarning] = useState(false);
    const paymentFieldsHydrated = useRef(false);
    const pendingPaymentTypeRef = useRef<string>('');
    const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

    const hydratePaymentFields = (profilePaymentType: string, phone: string) => {
        if (paymentFieldsHydrated.current) return;
        setPaymentType(profilePaymentType);
        setSavedPaymentType(profilePaymentType);
        setAthPhoneNumber(phone);
        pendingPaymentTypeRef.current = profilePaymentType;
        paymentFieldsHydrated.current = true;
    };

    const syncPaymentFieldsAfterSave = (nextPaymentType: string, phone?: string) => {
        setPaymentType(nextPaymentType);
        setSavedPaymentType(nextPaymentType);
        pendingPaymentTypeRef.current = nextPaymentType;
        if (phone !== undefined) {
            setAthPhoneNumber(phone);
        }
    };

    // Check if device is small (mobile)
    useEffect(() => {
        const checkDeviceSize = () => {
            setIsSmallDevice(window.innerWidth < 768); // 768px is typical tablet breakpoint
        };
        
        checkDeviceSize();
        window.addEventListener('resize', checkDeviceSize);
        
        return () => window.removeEventListener('resize', checkDeviceSize);
    }, []);



    // Set initial values for additional profile fields
    useEffect(() => {
        // Only fetch worker profile data if user is a worker
        if (user?.type === 'worker') {
            const fetchWorkerProfile = async () => {
                try {
                    const user = await api.getProfileDetails();
                    if (user && user.success) {
                        const workerProfile = user.data?.workerProfile;
                        
                        if (workerProfile) {
                            setHourlyPay(workerProfile.hourlyPay?.toString() || '');
                            setPhysicalAddress({
                                line1: workerProfile.physicalAddress?.line1 || '',
                                line2: workerProfile.physicalAddress?.line2 || '',
                                city: workerProfile.physicalAddress?.city || '',
                                zipCode: workerProfile.physicalAddress?.zipCode || ''
                            });
                            setPostalAddress(workerProfile.postalAddress || '');
                            const profilePaymentType = workerProfile.paymentType || '';
                            hydratePaymentFields(profilePaymentType, workerProfile.athPhoneNumber || '');
                            
                            // Set Google location if available
                            if (workerProfile.location) {
                                const locationBody = {
                                    label: workerProfile.locationName,
                                    value: {
                                        place_id: workerProfile.location?.value?.place_id,
                                        terms: workerProfile.location?.value?.terms,
                                    },
                                    coordinates: workerProfile.location?.coordinates,
                                };
                                setGoogleLocation(locationBody);
                            }
                            
                            // Check if profile is complete
                            const complete = isWorkerProfileComplete(workerProfile);
                            const missing = getMissingFields(workerProfile);
                            setIsProfileComplete(complete);
                            setMissingFields(missing);
                            setAgeConfirmed(user.data?.isAgeConfirmed === true);
                        }
                    }
                } catch (error) {
                 console.log("error:",error)
                }   
            };

            fetchWorkerProfile();
        } else {
            // For non-workers, always set to true
            setIsProfileComplete(true);
        }
    }, [user?.type]);

    // Fetch all data on mount
    useEffect(() => {

        if(user?.type === 'worker'){
        const fetchAll = async () => {
            const [cat, serv, prof, fetchedRegions] = await Promise.all([
                api.getServiceCategoriesForSettings(),
                api.getServicesForSettings(),
                api.getProfileDetails(),
                api.getRegions()
            ]);
            setCategories(cat);
            setAllServices(serv);
            setRegions(fetchedRegions);
            
            // Transform regions into grouped options for react-select
            const grouped = fetchedRegions.map(region => ({
                label: region.name,
                options: region.municipalities.map(muni => ({
                    value: `${region._id}:${muni}`,
                    label: muni,
                    regionId: region._id,
                    regionName: region.name
                }))
            }));
            const allOfPROption = {
                value: 'ALL_OF_PR',
                label: 'All of Puerto Rico',
                isAllOfPR: true
            };
            const allGroupedOptions = [allOfPROption, ...grouped];
            setGroupedRegionOptions(allGroupedOptions);
            
            // Get all municipality options for comparison (use local variable, not state)
            const allMunicipalityOptions = grouped
                .flatMap(opt => opt.options || []);
            const allValues = allMunicipalityOptions.map(opt => opt.value);
            
            if (prof && prof.success && prof.data?.workerProfile) {
                setSelectedCategoryId(prof.data.workerProfile.serviceCategory || "");
                setSelectedServiceIds(prof.data.workerProfile.servicesOffered || []);
                setSelectedSkills(prof.data.workerProfile.skills || []);
                setAvailability(prof.data.workerProfile.availability || "");
                setDescription(prof.data.workerProfile.description || "");
                const profilePaymentType = prof.data.workerProfile.paymentType || "";
                hydratePaymentFields(profilePaymentType, prof.data.workerProfile.athPhoneNumber || "");
                
                // Load existing regions from profile
                // Check if RegionDetails exists (preferred - has full info with subregions)
                if (prof.data.workerProfile.RegionDetails && prof.data.workerProfile.RegionDetails.length > 0) {
                    
                    // Transform RegionDetails back to select format
                    const regionOptions: any[] = [];
                    prof.data.workerProfile.RegionDetails.forEach((regionDetail: any) => {
                        const region = fetchedRegions.find(r => r._id === regionDetail.regionId || r._id === regionDetail.id);
                        if (region && regionDetail.subregions) {
                            regionDetail.subregions.forEach((subregion: string) => {
                                regionOptions.push({
                                    value: `${regionDetail.regionId || regionDetail.id}:${subregion}`,
                                    label: subregion,
                                    regionId: regionDetail.regionId || regionDetail.id,
                                    regionName: region.name
                                });
                            });
                        }
                    });
                    
                    // Check if all municipalities are selected (All of Puerto Rico)
                    const selectedValues = regionOptions.map(opt => opt.value);
                    const isAllOfPR = selectedValues.length === allValues.length && 
                                     selectedValues.every(val => allValues.includes(val));
                    
                    if (isAllOfPR && allOfPROption) {
                        // Show "All of Puerto Rico" instead of all individual municipalities
                        setSelectedMunicipalities([allOfPROption]);
                        setSelectedRegions(allValues);
                    } else {
                        setSelectedMunicipalities(regionOptions);
                        setSelectedRegions(selectedValues);
                    }
                } 
                // Fallback: If RegionDetails not available, try to reconstruct from Regions array
                else if (prof.data.workerProfile.Regions && prof.data.workerProfile.Regions.length > 0) {
                    // Regions is an array of region IDs, so we need to match with fetchedRegions
                    // and include all municipalities for those regions
                    const regionOptions: any[] = [];
                    prof.data.workerProfile.Regions.forEach((regionId: string) => {
                        const region = fetchedRegions.find(r => r._id === regionId || r._id.toString() === regionId.toString());
                        if (region && region.municipalities) {
                            region.municipalities.forEach((subregion: string) => {
                                regionOptions.push({
                                    value: `${region._id}:${subregion}`,
                                    label: subregion,
                                    regionId: region._id,
                                    regionName: region.name
                                });
                            });
                        }
                    });
                    
                    // Check if all municipalities are selected (All of Puerto Rico)
                    const selectedValues = regionOptions.map(opt => opt.value);
                    const isAllOfPR = selectedValues.length === allValues.length && 
                                     selectedValues.every(val => allValues.includes(val));
                    
                    if (isAllOfPR && allOfPROption) {
                        // Show "All of Puerto Rico" instead of all individual municipalities
                        setSelectedMunicipalities([allOfPROption]);
                        setSelectedRegions(allValues);
                    } else {
                        setSelectedMunicipalities(regionOptions);
                        setSelectedRegions(selectedValues);
                    }
                } 
                // Check if profile is complete after loading all data
                if (prof && prof.success && prof.data?.workerProfile) {
                    const complete = isWorkerProfileComplete(prof.data.workerProfile);
                    const missing = getMissingFields(prof.data.workerProfile);
                    setIsProfileComplete(complete);
                    setMissingFields(missing);
                    setAgeConfirmed(prof.data?.workerProfile?.isAgeConfirmed === true);
                }
            }
        };
        fetchAll();
    }

    }, [user?.type]);

    // Refetch localized catalogs when language changes (labels use API `name`).
    // Skip the initial language value — mount effects already load catalogs.
    const prevCatalogLangRef = useRef(i18n.language);
    useEffect(() => {
        if (user?.type !== "worker") return;
        if (prevCatalogLangRef.current === i18n.language) return;
        prevCatalogLangRef.current = i18n.language;

        let cancelled = false;
        const refetchCatalogs = async () => {
            try {
                const [cat, serv, fetchedRegions] = await Promise.all([
                    api.getServiceCategoriesForSettings(),
                    api.getServicesForSettings(),
                    api.getRegions(),
                ]);
                if (cancelled) return;
                setCategories(cat);
                setAllServices(serv);
                setServices(serv);
                setServiceCategories(cat);
                setRegions(fetchedRegions);

                const grouped = fetchedRegions.map((region) => ({
                    label: region.name,
                    options: region.municipalities.map((muni) => ({
                        value: `${region._id}:${muni}`,
                        label: muni,
                        regionId: region._id,
                        regionName: region.name,
                    })),
                }));
                const allOfPROption = {
                    value: "ALL_OF_PR",
                    label: "All of Puerto Rico",
                    isAllOfPR: true,
                };
                setGroupedRegionOptions([allOfPROption, ...grouped]);
            } catch (error) {
                console.log("error:", error);
            }
        };

        refetchCatalogs();
        return () => {
            cancelled = true;
        };
    }, [i18n.language, user?.type]);

    // Get services for selected category
  const categoryServices = React.useMemo(() => {
    if (user?.type !== "worker") return [];

    const cat = categories.find(c => c._id === selectedCategoryId);
    return cat ? cat.services : [];
}, [categories, selectedCategoryId, user?.type]);


    useEffect(() => {

        const fetchInitialData = async () => {
            const services = await api.getServicesForSettings();
            setServices(services);
            const fetchedServiceCategories = await api.getServiceCategoriesForSettings();
            setServiceCategories(fetchedServiceCategories);
            const user = await api.getProfileDetails();
            if (user && user.success) {
                addDatatoForm(user.data);
            }

        };
        if(user?.type ==='worker'){
        fetchInitialData();
        }

    }, []);

    const addDatatoForm = (user: any) => {
        setWorkerProfile(user?.workerProfile);
        setSelectedCategoryId(user?.workerProfile?.serviceCategory || "");
        // Convert skills (Skill[]) to string[] of IDs if present
        setSelectedSkills((user?.workerProfile?.skills || []).map((s: any) => typeof s === 'string' ? s : s._id));
        // selectedServices will be set after categoryServices loads
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleProfileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
    };

    const resizeImage = async (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
        toast.error("The profile photo cannot exceed 10MB");
        return;
      }
    
        return new Promise<Blob>((resolve) => {
          Resizer.imageFileResizer(
            file,
            1500,
            1500,
            'JPEG',
            90,
            0,
            (uri: any) => {
              resolve(uri);
            },
            'blob'
          );
        });
      };

    // Profile Picture Handler
    const handleImgPreview = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error("Profile picture cannot exceed 10MB");
            return;
        }

        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImgPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    useEffect(() => {
        if (user) {
            setProfileForm({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                avatar: user?.avatar || user?.data?.avatar || '',
            });
            setImgPreview(user?.data?.avatar || user?.avatar || '');
            setSmsNotificationsOptIn(user?.isSmsSent ?? false);
        }
    }, [user]);



    // When selectedCategoryId or services change, update categoryServices
    useEffect(() => {
         if (user?.type !== "worker") return;
        const selectedCategory = serviceCategories.find((cat: any) => {
            return cat.id === selectedCategoryId
        });
        // setCategoryServices(selectedCategory?.services || []); // This line is removed as per the new_code
    }, [selectedCategoryId, serviceCategories]);

    // Pre-select services after categoryServices and workerProfiles are loaded
    useEffect(() => {
        if (selectedCategoryId && workerProfiles && categoryServices.length > 0) {
            // servicesOffered may be array of IDs or objects
            const offeredIds = (workerProfiles.servicesOffered || []).map((s: any) => typeof s === 'string' ? s : s._id);
            const selected = categoryServices
                .filter(service => offeredIds.includes(service._id))
                .map(service => ({ value: service._id, label: service.name }));
            setSelectedServices(selected);
        }
    }, [categoryServices, selectedCategoryId, workerProfiles]);

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        const passwordForm = e.target as HTMLFormElement;
        const passwordData = new FormData(passwordForm);     // single value
        if (!passwordData.get("oldPassword") || !passwordData.get("newPassword") || !passwordData.get("confirmPassword")) {
            toast.error("Please enter a password.");
            return;
        }

        if (passwordData.get("newPassword") !== passwordData.get("confirmPassword")) {
            toast.error("Passwords don't match!");
            return;
        }
        setIsSubmitting(true);
        const res = await api.changePassword({
            password: form.oldPassword,
            newPassword: form.newPassword,
        })
        if (res.success) {
            toast.success('Password changed successfully');
            setIsSubmitting(false)
            setForm({
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
        }
        else {
            toast.error(res?.message);
            setIsSubmitting(false)
        }
    }

    const handlePersonalDetails = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const profileForm = e.target as HTMLFormElement;
        const profileData = new FormData(profileForm);
        setIsProfileSubmitting(true);
        // Handle profile picture upload first
        const avatarImg = profileData.get("avatar") as File;
        
        if (avatarImg && avatarImg.size) {
            try {
                const resizedImage = await resizeImage(avatarImg);
                const avatarFormData = new FormData();
                avatarFormData.set("file", resizedImage);
                const userWithAvatarImg = await api.updateAvatarPicture(avatarFormData);
                dispatch(updateUser({ ...userWithAvatarImg }));
                // toast.success("Profile picture updated successfully!");
            } catch (error) {
                toast.error("Failed to update profile picture. Please try again.");
                return;
            }
        }

        if (!profileData.get("first_name") || !profileData.get("last_name")) {
            toast.error("Por favor ingrese un nombre y apellido.");
            return;
        }

        try {
            if (user?.type === 'worker') {
                // Worker-specific validation and API call
                // Age confirmation required for providers
                if (!ageConfirmed) {
                    toast.error(t('ageConfirmationRequired') || 'You must confirm that you are 18 or older to update your profile.');
                    setIsProfileSubmitting(false);
                    return;
                }
                // Validate hourly pay

                if (hourlyPay && (parseFloat(hourlyPay) <= 0 || !Number.isInteger(parseFloat(hourlyPay)))) {
                    toast.error("Hourly rate must be a positive integer");
                    setIsProfileSubmitting(false);
                    return;
                }

                if (hourlyPay && parseFloat(hourlyPay) > 1000) {
                    toast.error("Hourly rate cannot exceed $1000");
                    setIsProfileSubmitting(false);
                    return;
                }

                // Validate location

                if (!googleLocation) {
                    toast.error("Please provide a valid location!");
                    setIsProfileSubmitting(false);
                    return;
                }

                   // Validate location

                if (!description) {
                    toast.error("Please provide a valid description!");
                    setIsProfileSubmitting(false);
                    return;
                }

                // Get geocode from selected location
                const [locationGeoCode] = await geocodeByPlaceId(
                    googleLocation.value.place_id
                ).catch((error) => {
                    toast.error("Error getting location. Please try again.");
                    throw error;
                });

                // Get longitude and latitude
                const locationLat = locationGeoCode.geometry.location.lat();
                const locationLong = locationGeoCode.geometry.location.lng();
                const locationCoordinates = [locationLong, locationLat];
                console.log("locationCoordinates",locationCoordinates)
                const payload = {
                    first_name: profileData.get("first_name") as string,
                    last_name: profileData.get("last_name") as string,
                    hourlyPay: hourlyPay ? parseInt(hourlyPay) : null,
                    description: description,
                    physicalAddress: {
                        ...physicalAddress,
                        city: googleLocation?.label || physicalAddress.city,
                    },
                    postalAddress,
                    location: {
                        type: "Point",
                        coordinates: locationCoordinates,
                        value: {
                            place_id: googleLocation.value.place_id,
                            terms: googleLocation.value.terms,
                        },
                    },
                    locationName: googleLocation.value.terms
                        .slice(0, 2)
                        .map((term) => term.value)
                        .join(", "),
                    isAgeConfirmed: ageConfirmed,
                };
                console.log("payload", payload)
                const res = await api.updateWorkerProfile(payload);
                if (res && res.success) {
                    toast.success("Profile updated successfully!");
                    dispatch(updateUser({ ...payload, isSmsSent: smsNotificationsOptIn }));
                    // Sync user-level SMS preference to backend
                    await api.updateProfileDetails({
                        first_name: profileData.get("first_name") as string,
                        last_name: profileData.get("last_name") as string,
                        isSmsSent: smsNotificationsOptIn,
                    });
                    
                    // Re-fetch worker profile to check if it's now complete
                    if (user?.type === 'worker') {
                        try { 
                            const updatedProfile = await api.getMyWorkerProfile();
                            if (updatedProfile) {
                                const complete = isWorkerProfileComplete(updatedProfile);
                                const missing = getMissingFields(updatedProfile);
                                setIsProfileComplete(complete);
                                setMissingFields(missing);
                                
                                if (complete && !isProfileComplete) {
                                    setIsProfileSubmitting(false);
                                    toast.success("Profile completed! Reloading page...");
                                    setTimeout(() => {
                                        window.location.reload();
                                    }, 500);
                                    return;
                                }
                            }
                        } catch (err) {
                            console.error("Error re-fetching profile:", err);
                        }
                    }
                    
                    setIsProfileSubmitting(false);
                } else {
                    toast.error(res?.message || "Something went wrong. Please try again.");
                }
            } else {
                // Regular user - only update basic profile
                const payload = {
                    first_name: profileData.get("first_name") as string,
                    last_name: profileData.get("last_name") as string,
                    isSmsSent: smsNotificationsOptIn,
                };

                const res = await api.updateProfileDetails(payload);
                if (res && res.success) {
                    toast.success("Profile updated successfully!");
                    dispatch(updateUser(payload));
                    setIsProfileSubmitting(false);
                } else {
                    toast.error(res?.message || "Something went wrong. Please try again.");
                }
            }
        } catch (err) {
            toast.error(
                getErrorMessage(err, "Failed to update profile. Please try again.")
            );
        } finally {
            setIsProfileSubmitting(false);
        }
    }

    const handleSkillsAndServices = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Validation
        // if (!selectedServiceIds.length) {
        //     toast.error('Por favor seleccione al menos un servicio.');
        //     return;
        // }
        // if (!selectedSkills.length) {
        //     toast.error('Por favor seleccione al menos una habilidad.');
        //     return;
        // }
        // if (!availability) {
        //     toast.error('Por favor seleccione la disponibilidad.');
        //     return;
        // }
        // if (!description.trim()) {
        //     toast.error('Por favor ingrese una descripción.');
        //     return;
        // }
        setIsSkillsSubmitting(true);
        try {
            // Transform selectedRegions to grouped format (same as signup)
            const groupedRegions: { id: string; subregions: string[] }[] = [];
            selectedRegions.forEach((item) => {
                const [regionId, subregion] = item.split(":");
                const existing = groupedRegions.find(r => r.id === regionId);
                if (existing) {
                    existing.subregions.push(subregion);
                } else {
                    groupedRegions.push({ id: regionId, subregions: [subregion] });
                }
            });
            
            // Extract just the region IDs for Regions field (backend expects array of ObjectIds)
            const regionIds = groupedRegions.map(r => r.id);
            
            // Also prepare RegionDetails for full region information (if backend needs it)
            const RegionDetails = groupedRegions.map(r => ({
                regionId: r.id,
                subregions: r.subregions,
            }));
            
            const payload = {
                skills: selectedSkills,
                servicesOffered: selectedServiceIds,
                serviceCategory: selectedCategoryId,
                Regions: regionIds,  // Array of region IDs only (ObjectIds)
                RegionDetails: RegionDetails,  // Full region details with subregions
                availability,
                description,
            };
            const res = await api.updateMyWorkerServices(payload);
            if (res && res.success) {
                toast.success("Skills and services updated successfully!");
                
                        // Re-fetch worker profile to check if it's now complete
                        if (user?.type === 'worker') {
                            try {
                                const updatedProfile = await api.getMyWorkerProfile();
                                if (updatedProfile) {
                                    const complete = isWorkerProfileComplete(updatedProfile);
                                    const missing = getMissingFields(updatedProfile);
                                    setIsProfileComplete(complete);
                                    setMissingFields(missing);
                                    
                                    if (complete && !isProfileComplete) {
                                        setIsSkillsSubmitting(false);
                                        toast.success("Profile completed! Reloading page...");
                                        setTimeout(() => {
                                            window.location.reload();
                                        }, 500);
                                        return;
                                    }
                                }
                            } catch (err) {
                                console.error("Error re-fetching profile:", err);
                            }
                        }
            } else {
                toast.error(res?.message || "Something went wrong. Please try again.");
            }
            // Optionally update redux store or local state
        } catch (err) {
            toast.error(
                getErrorMessage(err, "Error updating profile. Please try again.")
            );
        } finally {
            setIsSkillsSubmitting(false);
        }
    };

    const submitPaymentSettings = async (paymentTypeToSave?: string) => {
        const nextPaymentType = paymentTypeToSave || pendingPaymentTypeRef.current || paymentType;
        setIsPaymentTabSubmitting(true);
        try {
            const payload: Record<string, unknown> = {
                paymentType: nextPaymentType as 'STRIPE' | 'ATH',
            };
            if (nextPaymentType === 'ATH') {
                const digitsOnly = athPhoneNumber.replace(/\D/g, '');
                const localNumber = digitsOnly.startsWith('1')
                    ? digitsOnly.slice(1)
                    : digitsOnly;
                payload.athPhoneNumber = localNumber;
            }
            const res = await api.updateWorkerProfile(payload);
            if (res?.success) {
                toast.success(t('profileUpdatedSuccessfully') || 'Payment settings updated successfully.');
                syncPaymentFieldsAfterSave(
                    nextPaymentType,
                    nextPaymentType === 'ATH' ? String(payload.athPhoneNumber || '') : undefined
                );
                dispatch(updateUser({
                    paymentType: nextPaymentType,
                    workerProfile: {
                        ...(user?.workerProfile || {}),
                        paymentType: nextPaymentType,
                        ...(nextPaymentType === 'ATH'
                            ? { athPhoneNumber: payload.athPhoneNumber }
                            : {}),
                    },
                }));
                try {
                    const updatedProfile = await api.getMyWorkerProfile();
                    if (updatedProfile) {
                        const complete = isWorkerProfileComplete(updatedProfile);
                        const missing = getMissingFields(updatedProfile);
                        setIsProfileComplete(complete);
                        setMissingFields(missing);
                        if (complete && !isProfileComplete) {
                            toast.success("Profile completed! Reloading page...");
                            setTimeout(() => {
                                window.location.reload();
                            }, 500);
                        }
                    }
                } catch (err) {
                    console.error("Error re-fetching profile after payment update:", err);
                    const localProfile = {
                        ...(user?.workerProfile || {}),
                        paymentType: nextPaymentType as 'STRIPE' | 'ATH',
                        ...(nextPaymentType === 'ATH'
                            ? { athPhoneNumber: String(payload.athPhoneNumber || '') }
                            : {}),
                    };
                    setIsProfileComplete(isWorkerProfileComplete(localProfile as any));
                    setMissingFields(getMissingFields(localProfile as any));
                }
            } else {
                toast.error(res?.message || 'Failed to update payment settings.');
            }
        } catch (err) {
            toast.error(
                getErrorMessage(err, 'Failed to update payment settings.')
            );
        } finally {
            setIsPaymentTabSubmitting(false);
        }
    };

    const handlePaymentTabSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (user?.type !== 'worker') return;
        const nextPaymentType = pendingPaymentTypeRef.current || paymentType;
        if (nextPaymentType !== 'STRIPE' && nextPaymentType !== 'ATH') {
            toast.error(t('selectPaymentType'));
            return;
        }
        if (nextPaymentType === 'ATH') {
            const trimmed = athPhoneNumber.replace(/\D/g, '');
            if (!trimmed || trimmed.length < 10) {
                toast.error(t('athPhoneInvalid') || 'Please enter a valid ATH phone number');
                return;
            }
        }
        if (savedPaymentType === 'ATH' && nextPaymentType === 'STRIPE') {
            pendingPaymentTypeRef.current = 'STRIPE';
            setShowStripeSwitchWarning(true);
            return;
        }
        await submitPaymentSettings(nextPaymentType);
    };

    const handleConfirmStripeSwitch = async () => {
        setShowStripeSwitchWarning(false);
        await submitPaymentSettings('STRIPE');
    };

    // Checkbox option for react-select
    const Option = (props) => (
        <components.Option {...props}>
            {/* <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                style={{ marginRight: 8 }}
              /> */}
            <label>{props.label}</label>
        </components.Option>
    );
    const tab0Incomplete =
  missingFields.includes('location') ||
  missingFields.includes('description');

const tab2Incomplete =
  missingFields.includes('regions') ||
  missingFields.includes('services');

const tab3Incomplete = missingFields.includes('payment');

    return (
        <ContentContainer className='!px-2 m-0 bg-gray-100'>
            <h1 className='text-2xl text-black font-bold mb-5'> {t('settings')}</h1>
            <Box className="bg-white rounded-xl border border-gray-dark">
                {user?.type === 'worker' &&
                    <Tabs
                        className='w-full rounded-l-xl rounded-r-xl rounded-b-none border border-b-2 !border-left-0 border-top-0'
                        value={tab}
                        allowScrollButtonsMobile
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons={isSmallDevice}
                        slotProps={{ indicator: { style: { display: 'none' } } } }
                    >
                        <Tab
                            label={t('myProfile')}
                            sx={{
                                background: tab === 0 ? '#ff84800D' :  tab0Incomplete
        ? '#ef4444'
        : '#f3f4f6' ,
                                color: tab === 0 ? '#000' : '#222',
                                fontWeight: tab === 0 ? 600 : 500,
                                fontSize: 16,
                                transition: 'background 0.2s, color 0.2s',
                                border: '1px solid #e5e7eb',
                                borderRight: '1px solid #e5e7eb',
                                borderBottom: '1px solid #e5e7eb',
                                minHeight: '48px',
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    background: '#ff84800D',
                                    color: '#000',
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    background: tab === 0 ? '#ff84800D' : '#e5e7eb',
                                }
                            }}
                        />

                        <Tab
                            label={t('myCertifications')}
                            sx={{
                                background: tab === 1 ? '#ff84800D' : '#f3f4f6',
                                color: tab === 1 ? '#000' : '#222',
                                fontWeight: tab === 1 ? 600 : 500,
                                fontSize: 16,
                                transition: 'background 0.2s, color 0.2s',
                                border: '1px solid #e5e7eb',
                                borderRight: '1px solid #e5e7eb',
                                borderBottom: '1px solid #e5e7eb',
                                minHeight: '48px',
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    background: '#ff84800D',
                                    color: '#000',
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    background: tab === 1 ? '#ff84800D' : '#e5e7eb',
                                }
                            }}
                        />

                        <Tab
                            label={t('regionAndServices')}
                            sx={{
                                background: tab === 2 ? '#ff84800D' : tab2Incomplete
        ? '#ef4444'
        : '#f3f4f6' ,
                                color: tab === 2 ? '#000' : '#222',
                                fontWeight: tab === 2 ? 600 : 500,
                                fontSize: 16,
                                transition: 'background 0.2s, color 0.2s',
                                border: '1px solid #e5e7eb',
                              
                                borderRight: '1px solid #e5e7eb',
                                borderBottom: '1px solid #e5e7eb',
                                minHeight: '48px',
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    background: '#ff84800D',
                                    color: '#000',
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    background: tab === 2 ? '#ff84800D' : '#e5e7eb',
                                }
                            }}
                        />

                        <Tab
                            label={t('payment')}
                            sx={{
                                background: tab === 3 ? '#ff84800D' : tab3Incomplete
                                    ? '#ef4444'
                                    : '#f3f4f6',
                                color: tab === 3 ? '#000' : '#222',
                                fontWeight: tab === 3 ? 600 : 500,
                                fontSize: 16,
                                transition: 'background 0.2s, color 0.2s',
                                border: '1px solid #e5e7eb',
                                borderRight: '1px solid #e5e7eb',
                                borderBottom: '1px solid #e5e7eb',
                                minHeight: '48px',
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    background: '#ff84800D',
                                    color: '#000',
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    background: tab === 3 ? '#ff84800D' : '#e5e7eb',
                                }
                            }}
                        />

                    </Tabs>}
                
                {/* Show banner with missing fields if profile is incomplete */}
                {user?.type === 'worker' && !isProfileComplete && missingFields.length > 0 && (
                    <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700 mx-6 mt-4 rounded-lg" role="alert">
                        <p className="font-bold mb-2">{t('profileIncomplete') || 'Profile Incomplete'}</p>
                        <p className="text-sm mb-2">{t('missingFieldsMessage') || 'The following required fields are missing:'}</p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                            {missingFields.map((field) => (
                                <li key={field}>
                                    {field === 'location' && t('location')}
                                    {field === 'services' && t('services')}
                                    {field === 'regions' && t('regions')}
                                    {field === 'description' && t('description')}
                                    {field === 'payment' && t('payment')}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {tab === 0 && (
                    <div className='p-6 flex flex-col gap-8'>

                        {/* Profile Picture Section */}
                        <div className='flex flex-col gap-3 w-full'>
                            <h1 className='text-2xl text-black font-bold'>{t('profilePhoto')}</h1>
                            <Divider className='border border-gray-dark' />
                            
                            <div className='flex flex-col items-center gap-4'>
                                {/* <label className='text-black font-medium text-sm'>Update your profile picture</label> */}
                                
                                <div className='flex justify-center'>
                                    <label htmlFor="profileImg" style={{ cursor: "pointer" }}>
                                        {imgPreview || profileForm?.avatar ? (
                                            <img
                                                src={imgPreview || profileForm?.avatar}
                                                alt="Profile"
                                                width={120}
                                                height={120}
                                                style={{
                                                    objectFit: "cover",
                                                    height: "120px",
                                                    width: "120px",
                                                    borderRadius: "50%",
                                                    border: "3px solid #e5e7eb"
                                                }}
                                            />
                                        ) : (
                                            <div className="w-[120px] h-[120px] rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                                                <SVGUploadLogoImage />
                                            </div>
                                        )}
                                    </label>
                                </div>
                                
                                <p
                                    className="text-xs text-gray-500 text-center"
                                    dangerouslySetInnerHTML={{ __html: t('uploadPhotoInstruction') }}
                                ></p>
                            </div>
                        </div>

                        <div className='flex flex-col gap-3 w-full'>
                            <h1 className='text-2xl text-black font-bold'>{t('personalInfo')}</h1>
                            <Divider className='border border-gray-dark' />

                            <form onSubmit={handlePersonalDetails}>
                                {/* Hidden file input inside the form */}
                                <input
                                    id="profileImg"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImgPreview}
                                    name="avatar"
                                    className="d-none"
                                />
                                
                                <div className='flex flex-col gap-5 '>
                                    <div className='flex items-start gap-5 w-full'>
                                        <Input
                                            label={t('firstName')}
                                            labelClassName='!text-black !font-medium'
                                            className='w-full !rounded-none'
                                            name="first_name"
                                            type="text"
                                            maxLength={15}
                                            value={profileForm.first_name}
                                            onChange={handleProfileInputChange}
                                            inputClassName='py-4'
                                            variant="normal"
                                            required
                                        />

                                        <Input
                                            label={t('lastName')}
                                            className='w-full'
                                            labelClassName='!text-black !font-medium'
                                            name="last_name"
                                            maxLength={15}
                                            type="text"
                                            value={profileForm.last_name}
                                            onChange={handleProfileInputChange}
                                            inputClassName='py-4'
                                            variant="normal"
                                            required
                                        />
                                    </div>




                                    <div className='flex items-start gap-5'>
                                        <Input
                                            label={t('byEmail')}
                                            className='w-full'
                                            labelClassName='!text-black !font-medium'
                                            name="email"
                                            type="text"
                                            value={user?.email}
                                            onChange={handleInputChange}
                                            inputClassName='py-4'
                                            disabled
                                        />

                                        <div className='w-full'>
                                            <label className='text-black font-normal text-sm pb-2'>{t('phoneNumber')}</label>
                                            <PhoneInput
                                                country={'us'}
                                                disabled
                                                countryCodeEditable={false}
                                                value={user?.phone}
                                                onChange={value => {
                                                    setPhone(value);
                                                    setPhoneError('');
                                                }}
                                                inputProps={{
                                                    name: 'phone',
                                                    required: true,
                                                    id: 'registerUserPhone',
                                                    style: { flex: 6, backgroundColor: '#e9ecef', borderRadius: '7px', width: '100%', minHeight: '58px', fontSize: '14px' }
                                                }}
                                                inputStyle={{
                                                    width: '100%',
                                                    borderRadius: '7px',
                                                    minHeight: '58px',
                                                    fontSize: '14px'
                                                }}
                                                buttonStyle={{
                                                    borderRadius: '7px',
                                                }}
                                                containerStyle={{
                                                    width: '100%',

                                                }}
                                                specialLabel=""

                                                disableDropdown={false}
                                                onlyCountries={['us']}
                                                onBlur={() => {
                                                    if (phone && phone.replace(/\D/g, '').length < 11) setPhoneError('Por favor ingrese un número de teléfono válido.');
                                                }}
                                            />
                                            {/* SMS unsubscribe - directly below phone field */}
                                            <div className="flex flex-col gap-2 w-full" style={{ marginTop: '12px' }}>
                                                <div className="flex items-center gap-2" style={{ alignItems: 'flex-start' }}>
                                                    <input
                                                        type="checkbox"
                                                        id="settingsSmsOptIn"
                                                        checked={smsNotificationsOptIn}
                                                        onChange={(e) => setSmsNotificationsOptIn(e.target.checked)}
                                                        style={{ marginTop: 2, flexShrink: 0 }}
                                                    />
                                                    <label htmlFor="settingsSmsOptIn" className="text-black font-medium text-sm cursor-pointer">
                                                        {t('settingsSmsNotificationsLabel')}
                                                    </label>
                                                </div>
                                                <p className="text-gray-500 text-xs ml-6">{t('settingsSmsNotificationsHelper')}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Worker-specific fields - only show for worker type */}
                                    {user?.type === 'worker' && (
                                        <>
                                            {/* Hourly Rate Field */}
                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('hourlyRateUsd')}</label>
                                                <div className='flex items-center gap-2'>
                                                    <Input
                                                        className='w-full'
                                                        labelClassName='!text-black !font-medium'
                                                        name="hourlyPay"
                                                        type="number"
                                                        min={0}
                                                        step="1"
                                                        value={hourlyPay}
                                                        onChange={(e) => setHourlyPay(e.target.value)}
                                                        placeholder="$25"
                                                        inputClassName='py-4'
                                                        variant="normal"
                                                        onWheel={(e) => e.currentTarget.blur()}
                                                    />
                                                    <span className='text-gray-600 font-medium'>USD</span>
                                                </div>
                                            </div>

                                            {/* Payment Type - read-only; edit on Payment tab */}
                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('receivePaymentsVia')}</label>
                                                <select
                                                    className="form-select py-4 bg-gray-100"
                                                    value={paymentType || ''}
                                                    disabled
                                                    aria-label={t('receivePaymentsVia')}
                                                >
                                                    <option value="">{t('selectPaymentType')}</option>
                                                    <option value="STRIPE">{t('paymentTypeStripe')}</option>
                                                    <option value="ATH">{t('paymentTypeAth')}</option>
                                                </select>
                                            </div>

                                            {/* Location Field */}
                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('cityLocation')} <span style={{ color: "red" }}> *</span></label>
                                                <GooglePlacesAutocomplete
                                                    apiKey={googlePlacesApiKey}
                                                    selectProps={{
                                                        value: googleLocation,
                                                        onChange: setGoogleLocation,
                                                        placeholder: "Buscar una ciudad...",
                                                    }}
                                                    autocompletionRequest={{
                                                        componentRestrictions: { country: 'PR' }
                                                    }}
                                                />
                                            </div>

                                            {/* Physical Address Section */}
                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('physicalAddress')}</label>
                                                <Input
                                                    className='w-full'
                                                    labelClassName='!text-black !font-medium'
                                                    name="physicalAddressLine1"
                                                    type="text"
                                                    value={physicalAddress.line1}
                                                    onChange={(e) => setPhysicalAddress({ ...physicalAddress, line1: e.target.value })}
                                                    placeholder={t('addressNumberEtc')}
                                                    inputClassName='py-4'
                                                    variant="normal"
                                                />
                                                <Input
                                                    className='w-full'
                                                    labelClassName='!text-black !font-medium'
                                                    name="physicalAddressLine2"
                                                    type="text"
                                                    value={physicalAddress.line2}
                                                    onChange={(e) => setPhysicalAddress({ ...physicalAddress, line2: e.target.value })}
                                                    placeholder={t('addressInfoOptional')}
                                                    inputClassName='py-4'
                                                    variant="normal"
                                                />
                                                <Input
                                                    className='w-full'
                                                    labelClassName='!text-black !font-medium'
                                                    name="physicalAddressZipCode"
                                                    type="text"
                                                    value={physicalAddress.zipCode}
                                                    onChange={(e) => setPhysicalAddress({ ...physicalAddress, zipCode: e.target.value })}
                                                    placeholder={t('postalCode')}
                                                    inputClassName='py-4'
                                                    variant="normal"
                                                />
                                            </div>

                                            {/* Postal Address Section */}
                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('poBoxOrFullAddress')}</label>
                                                <Input
                                                    className='w-full'
                                                    labelClassName='!text-black !font-medium'
                                                    name="postalAddress"
                                                    type="text"
                                                    value={postalAddress}
                                                    onChange={(e) => setPostalAddress(e.target.value)}
                                                    placeholder={t('poBoxOrFullAddress')}
                                                    inputClassName='py-4'
                                                    variant="normal"
                                                />
                                            </div>

                                            <div className='flex flex-col gap-2 w-full'>
                                                <label className='text-black font-medium text-sm'>{t('description/about')} <span style={{ color: "red" }}> *</span></label>
                                                <TextArea
                                                    className='w-full'
                                                    labelClassName='!text-black !font-medium'
                                                    name="description"
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    placeholder={t('enterDescription')}
                                                    inputClassName='py-4'
                                                    variant="normal"
                                                    required
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Age confirmation (Provider Profile only) */}
                                    {user?.type === 'worker' && (
                                        <div className='flex flex-col gap-2 w-full'>
                                            <div className="flex items-start gap-2" style={{ alignItems: "flex-start", gap: "8px" }}>
                                                <input
                                                    type="checkbox"
                                                    id="ageConfirmation"
                                                    checked={ageConfirmed}
                                                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                                                    style={{ marginTop: "4px", flexShrink: 0 }}
                                                />
                                                <label htmlFor="ageConfirmation" style={{ color: "#1e293b", fontSize: "14px", margin: 0, cursor: "pointer" }}>
                                                    {t('ageConfirmationCheckboxLabel')}
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    <CustomButton
                                        type="submit"
                                        label={t('updateProfile')}
                                        disabled={isProfileSubmitting}
                                        isLoading={isProfileSubmitting}
                                        className='!bg-darkBlue rounded-3xl  !py-3 ! mt-4 w-fit px-12 ml-auto'
                                        labelStyle='!text-white !font-medium'

                                    >
                                    </CustomButton>

                                </div>


                            </form>

                        </div>

                        {/* Change Password Section - Only show for non-Google users */}
                        {((!user?.isGoogleAuth && user?.type === 'worker') || (!user?.isGoogleAuth && user?.type !== 'worker')  ) && (
                        <div className='flex flex-col gap-3'>
                            <h1 className='text-2xl text-black font-bold'>{t('changePassword')}</h1>
                            <Divider className='border border-gray-dark' />
                            <form onSubmit={handleUpdatePassword} className="flex flex-col gap-6 mt-2">
                                <Input
                                    label={t('currentPassword')}
                                    labelClassName='!text-black !font-medium'
                                    name="oldPassword"
                                    type="password"
                                    value={form.oldPassword}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('enterCurrentPassword')}
                                    variant="normal"
                                    inputClassName='py-4'
                                />
                                <Input
                                    label={t('newPassword')}
                                    labelClassName='!text-black !font-medium'
                                    name="newPassword"
                                    type="password"
                                    value={form.newPassword}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('enterNewPassword')}
                                    variant="normal"
                                    inputClassName='py-4'
                                />
                                <Input
                                    label={t('confirmPassword')}
                                    labelClassName='!text-black !font-medium'
                                    name="confirmPassword"
                                    type="password"
                                    value={form.confirmPassword}
                                    onChange={handleInputChange}
                                    required
                                    placeholder={t('confirmNewPassword')}
                                    variant="normal"
                                    inputClassName='py-4'
                                />
                                <CustomButton
                                    type="submit"
                                    label={t('save')}
                                    isLoading={isSubmitting}
                                    disabled={isSubmitting}
                                    className='!bg-darkBlue rounded-3xl  !py-3 ! mt-4 w-fit px-12 ml-auto'
                                    labelStyle='!text-white !font-medium'

                                >
                                </CustomButton>
                            </form>
                        </div>
                        )}



                    </div>
                )}
                {tab === 1 && (
                    <Box>
                        <UpdateCertifications skipContainer={true} />
                    </Box>
                )}
                {tab === 2 && (
                    <div className='p-6 flex flex-col gap-8'>
                        <form onSubmit={handleSkillsAndServices}>
                            <div className='flex flex-col gap-3 w-full'>
                                <h1 className='text-2xl text-black font-bold'>{t('regionAndService')}</h1>
                                <Divider className='border border-gray-dark' />
                                
                                {/* Regions Multi-Select */}
                                <div className='flex flex-col gap-2 w-full'>
                                    <label style={{ color: "black" }}>{t('regions')} <span style={{ color: "red" }}> *</span></label>
                                    <ReactSelect
                                        isMulti
                                        name="regions"
                                        options={
                                            selectedMunicipalities.some(opt => opt.isAllOfPR)
                                                ? groupedRegionOptions.filter(opt => opt.isAllOfPR)
                                                : groupedRegionOptions
                                        }
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        value={
                                            selectedMunicipalities.some(opt => opt.isAllOfPR)
                                                ? [groupedRegionOptions.find(opt => opt.isAllOfPR)]
                                                : selectedMunicipalities
                                        }
                                        onChange={(selectedOptions) => {
                                            const allOfPROption = groupedRegionOptions.find(opt => opt.isAllOfPR);
                                            const allMunicipalityOptions = groupedRegionOptions
                                                .filter(opt => !opt.isAllOfPR)
                                                .flatMap(opt => opt.options || [opt]);

                                            if (selectedOptions.some(opt => opt.isAllOfPR)) {
                                                setSelectedMunicipalities([allOfPROption]);
                                                setSelectedRegions(allMunicipalityOptions.map(opt => opt.value));
                                                return;
                                            }

                                            if (selectedMunicipalities.some(opt => opt.isAllOfPR)) {
                                                setSelectedMunicipalities([]);
                                                setSelectedRegions([]);
                                                return;
                                            }

                                            setSelectedMunicipalities(selectedOptions || []);
                                            setSelectedRegions((selectedOptions || []).map(opt => opt.value));
                                        }}
                                        placeholder={t('selectServiceAreas')}
                                        closeMenuOnSelect={false}
                                        components={{ Option }}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderColor: "#ccc",
                                                color: "#696969",
                                                minHeight: '60px',
                                                borderRadius: '7px',
                                                backdropFilter: 'none',
                                                backgroundColor: '#fff',
                                                border: '1px solid #f0f5f7'
                                            }),
                                            input: (base) => ({
                                                ...base,
                                                color: "#696969"
                                            }),
                                            placeholder: (base) => ({
                                                ...base,
                                                color: "#696969",
                                                paddingLeft: 10,
                                                fontSize: 15
                                            }),
                                            singleValue: (base) => ({
                                                ...base,
                                                color: "#000"
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isFocused ? "#f0f0f0" : "#fff",
                                            }),
                                            multiValue: (base) => ({
                                                ...base,
                                                backgroundColor: "#f0f0f0",
                                                color: '#696969'
                                            }),
                                            multiValueLabel: (base) => ({
                                                ...base,
                                                color: "#696969"
                                            }),
                                            multiValueRemove: (base) => ({
                                                ...base,
                                                color: "#555",
                                                ':hover': {
                                                    backgroundColor: '#ddd',
                                                    color: '#000'
                                                }
                                            })
                                        }}
                                    />
                                </div>
                                
                                {/* Category Dropdown */}
                                <div className='flex flex-col gap-2 w-full'>
                                    <label style={{ color: "black" }}>{t('serviceCategory')} <span style={{ color: "red" }}> *</span></label>
                                    <select
                                        className="form-select py-4"
                                        required
                                        value={selectedCategoryId}
                                        onChange={e => {
                                            setSelectedCategoryId(e.target.value);
                                            setSelectedServiceIds([]);
                                            setSelectedSkills([]);
                                        }}
                                    >
                                        <option value="">{t('selectCategory')}</option>
                                        {categories.map(cat => (
                                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                {/* Services Multi-Select */}
                                {selectedCategoryId && (
                                    <div className='flex flex-col gap-2 w-full'>
                                        <label style={{ color: "black" }}>{t('servicesOffered')} <span style={{ color: "red" }}> *</span></label>
                                        <Select
                                            isMulti
                                            name="services"
                                            options={categoryServices.map(service => ({
                                                value: service._id,
                                                label: service.name
                                            }))}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            value={categoryServices
                                                .filter(service => selectedServiceIds.includes(service._id))
                                                .map(service => ({ value: service._id, label: service.name }))}
                                            onChange={selectedOptions => {
                                                const ids = Array.isArray(selectedOptions)
                                                    ? selectedOptions.map(opt => opt.value)
                                                    : [];
                                                
                                                // Find removed services by comparing previous and new selections
                                                const removedServices = selectedServiceIds.filter(id => !ids.includes(id));
                                                
                                                // Remove skills from removed services
                                                if (removedServices.length > 0) {
                                                    const skillsToRemove = removedServices.flatMap(serviceId => {
                                                        const serviceObj = allServices.find(s => s._id === serviceId);
                                                        return serviceObj?.skills?.map((skill: any) => skill._id) || [];
                                                    });
                                                    
                                                    setSelectedSkills(prev => 
                                                        prev.filter(skillId => !skillsToRemove.includes(skillId))
                                                    );
                                                }
                                                
                                                setSelectedServiceIds(ids);
                                            }}
                                            placeholder={t('selectServices')}
                                            closeMenuOnSelect={false}
                                            isDisabled={!selectedCategoryId}
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    borderColor: "#ccc",
                                                    color: "#696969",
                                                    minHeight: '60px',
                                                    borderRadius: '7px',
                                                    backdropFilter: 'none',
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #f0f5f7'
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    color: "#696969"
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color: "#696969",
                                                    paddingLeft: 10,
                                                    fontSize: 15
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color: "#000"
                                                }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isFocused ? "#f0f0f0" : "#fff",
                                                }),
                                                multiValue: (base) => ({
                                                    ...base,
                                                    backgroundColor: "#f0f0f0",
                                                    color: '#696969'
                                                }),
                                                multiValueLabel: (base) => ({
                                                    ...base,
                                                    color: "#696969"
                                                }),
                                                multiValueRemove: (base) => ({
                                                    ...base,
                                                    color: "#555",
                                                    ':hover': {
                                                        backgroundColor: '#ddd',
                                                        color: '#000'
                                                    }
                                                })
                                            }}
                                        />
                                    </div>
                                )}
                                {/* Skills Checkboxes */}
                               {selectedServiceIds.length > 0 && (
                        <div className='flex flex-col gap-2 w-full'>
                            <label style={{ color: "black" }}>
                                {t('skills')} <span style={{ color: "red" }}> *</span>
                            </label>

                            <table className="table table-bordered" style={{ background: "#fff" }}>
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{ width: "250px" }}>{t('service')}</th>
                                        <th>{t('skills')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {selectedServiceIds.map(serviceId => {
                                        const serviceObj = allServices.find(s => s._id === serviceId);
                                        if (!serviceObj) return null;

                                        return (
                                            <tr key={serviceObj._id}>
                                                {/* Service Name Column */}
                                                <td className="fw-semibold">{serviceObj.name}</td>

                                                {/* Skills Checkbox Column */}
                                                <td>
                                                    <div className="d-flex flex-wrap gap-4">
                                                        {serviceObj.skills?.map((skill) => (
                                                            <div key={skill._id} className="d-flex align-items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`skill-${skill._id}`}
                                                                    checked={selectedSkills.includes(skill._id)}
                                                                    onChange={() => {
                                                                        setSelectedSkills(prev =>
                                                                            prev.includes(skill._id)
                                                                                ? prev.filter(id => id !== skill._id)
                                                                                : [...prev, skill._id]
                                                                        );
                                                                    }}
                                                                />
                                                                <label htmlFor={`skill-${skill._id}`} style={{ color: "black" }}>
                                                                    {skill.name}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}


                                <div className='flex flex-col items-start gap-6 w-full'>
                                    <div className='flex flex-col gap-2 w-full'>
                                        <label htmlFor="availability" style={{ color: "black" }}>
                                            {t('availability')}
                                        </label>
                                        <select
                                            id="availability"
                                            name="availability"
                                            className="form-select py-4"
                                            value={availability}
                                            onChange={(e) => setAvailability(e.target.value)}
                                            required
                                        >
                                            <option value="" disabled>{t('selectAvailability')}</option>
                                            <option value="Monday to Friday">{t('mondayToFriday')}</option>
                                            <option value="Saturday to Sunday">{t('saturdayToSunday')}</option>
                                            <option value="Every day">{t('allDays')}</option>
                                        </select>
                                    </div>

                                    {/* <div className='flex flex-col gap-2 w-full'> */}
                                        {/* <TextArea
                                            name="description"
                                            label="Descripción"
                                            value={description}
                                            labelClassName='!text-black !text-base !font-normal'
                                            onChange={e => setDescription(e.target.value)}
                                            placeholder="Write"
                                            maxLength={150}
                                            className="w-full min-h-[120px] rounded text-base"
                                        /> */}
                                    {/* </div> */}
                                </div>


                                <CustomButton
                                    type="submit"
                                    label={t('save')}
                                    isLoading={isSkillsSubmitting}
                                    disabled={isSkillsSubmitting}
                                    className='!bg-darkBlue rounded-3xl  !py-3 ! mt-4 w-fit px-12 ml-auto'
                                    labelStyle='!text-white !font-medium'
                                />
                            </div>
                        </form>
                    </div>
                )}
                <Popup
                    isOpen={showStripeSwitchWarning}
                    handleClose={() => setShowStripeSwitchWarning(false)}
                    className="bg-white"
                    size="sm"
                    headerTitle={t('stripeSwitchWarningTitle')}
                    isShowCloseIcon
                    isShowBlackCrossIcon
                    isShowDivider
                    bodyChildren={
                        <div className="p-6 flex flex-col gap-4">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
                                <p className="font-semibold mb-2">{t('stripeSwitchWarningHeading')}</p>
                                <p>{t('stripeSwitchWarningMessage')}</p>
                            </div>
                            <div className="flex justify-end gap-3">
                                <CustomButton
                                    type="button"
                                    label={t('cancel')}
                                    className="!rounded-3xl border border-darkBlue bg-white px-6 py-3"
                                    labelStyle="text-darkBlue font-medium"
                                    handleButtonClick={() => setShowStripeSwitchWarning(false)}
                                />
                                <CustomButton
                                    type="button"
                                    label={t('continue')}
                                    className="!rounded-3xl !bg-darkBlue px-6 py-3"
                                    labelStyle="text-white font-medium"
                                    isLoading={isPaymentTabSubmitting}
                                    disabled={isPaymentTabSubmitting}
                                    handleButtonClick={handleConfirmStripeSwitch}
                                />
                            </div>
                        </div>
                    }
                />

                {tab === 3 && user?.type === 'worker' && (
                    <div className='p-6 flex flex-col gap-8'>
                        <div className='flex flex-col gap-3 w-full'>
                            <h1 className='text-2xl text-black font-bold'>{t('payment')}</h1>
                            <Divider className='border border-gray-dark' />
                        </div>
                        <form onSubmit={handlePaymentTabSubmit} className='flex flex-col gap-6'>
                            <div className='flex flex-col gap-2 w-full'>
                                <label className='text-black font-medium text-sm'>{t('receivePaymentsVia')}</label>
                                <select
                                    className="form-select py-4"
                                    value={paymentType}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setPaymentType(nextValue);
                                        pendingPaymentTypeRef.current = nextValue;
                                    }}
                                    aria-label={t('selectPaymentType')}
                                >
                                    <option value="">{t('selectPaymentType')}</option>
                                    <option value="STRIPE">{t('paymentTypeStripe')}</option>
                                    <option value="ATH">{t('paymentTypeAth')}</option>
                                </select>
                            </div>
                            {paymentType === 'ATH' && (
                                <div className='flex flex-col gap-2 w-full'>
                                    <label className='text-black font-medium text-sm'>{t('athPhoneNumberLabel')} <span style={{ color: "red" }}> *</span></label>
                                    <Input
                                        className='w-full'
                                        labelClassName='!text-black !font-medium'
                                        name="athPhoneNumber"
                                        type="tel"
                                        value={athPhoneNumber}
                                        onChange={(e) => setAthPhoneNumber(e.target.value)}
                                        placeholder={t('athPhonePlaceholder')}
                                        inputClassName='py-4'
                                        variant="normal"
                                    />
                                </div>
                            )}
                            <CustomButton
                                type="submit"
                                label={t('updateProfile')}
                                disabled={isPaymentTabSubmitting}
                                isLoading={isPaymentTabSubmitting}
                                className='!bg-darkBlue rounded-3xl !py-3 !mt-4 w-fit px-12 ml-auto'
                                labelStyle='!text-white !font-medium'
                            />
                        </form>
                    </div>
                )}
            </Box>
        </ContentContainer>
    );
};

export default SettingsPage;
